import { useEffect, useRef } from 'react';
import { enrichSelectedExtras } from '../../logic/enrichSelectedExtras';
import { useExperiencePricingConfig } from '../../hooks/useExperiencePricingConfig';
import type { NotifyFn, Translate } from './types';

interface ExtrasSyncParams {
  dayIndex: number;
  experience: any;
  tripFilters: any;
  priceObject: any;
  selectedExtras: any;
  setSelectedExtras: (extras: any[]) => void;
  notify?: NotifyFn;
  t: Translate;
}

// Backfills pricing on any pre-populated extra still missing it, once the
// program's price is known. (The legacy `initialExtras` hydration this hook
// used to carry alongside is gone: `toTripState` has returned an empty list
// since KD-3548, extras are read straight from the days.)
export function useTripDayExperienceExtrasSync({
  dayIndex,
  experience,
  tripFilters,
  priceObject,
  selectedExtras,
  setSelectedExtras,
  notify,
  t,
}: ExtrasSyncParams) {
  const processedKeysRef = useRef(new Set<string>());
  const { keepsSavedExtras } = useExperiencePricingConfig();

  useEffect(() => {
    if (!priceObject || keepsSavedExtras) return;

    const { extras, hasChanges, someUnavailable } = enrichSelectedExtras({
      selectedExtras,
      dayIndex,
      experience,
      priceObject,
      tripComposition: tripFilters?.composition,
      processedKeys: processedKeysRef.current,
    });

    if (hasChanges) {
      setSelectedExtras(extras);
    }

    if (someUnavailable) {
      notify?.(t('compose.someOptionsUnavailable'), { type: 'error' });
    }
    // `experience` is read whole from the closure; only the fields listed
    // decide a re-run (the object changes on every price write).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priceObject,
    keepsSavedExtras,
    selectedExtras,
    dayIndex,
    experience?.id,
    experience?.bestMatches,
    experience?.incompatibilities,
    tripFilters?.composition,
    setSelectedExtras,
    notify,
    t,
  ]);
}
